import { Flex, FlexItem, P, Popover } from "orcs-design-system";
import PropTypes from "prop-types";
import GroupTypeBadge from "src/components/Badge/GroupTypeBadge";
import { useGroupTypes } from "src/contexts/global/WorkspaceContext";
import PersistedMoveIndicator from "./PersistedMoveIndicator";

const ACCESSIBLE_LABEL =
  "This team's type was changed in TeamForm; the type is saved and may differ from the source hierarchy.";

const PersistentTypeExplanation = () => (
  <P>
    This is a persistent type change due to parent persistent move, meaning this
    team type has been changed within TeamForm and may not match the source
    system hierarchy. This team will continue to use this type until it is
    manually changed again.
  </P>
);

const PersistedMoveGroupTypeTooltipBody = ({
  previousGroupType,
  groupTypes,
}) => (
  <Flex flexDirection="column" gap="s">
    <P mb="0">This team type was changed from:</P>
    <GroupTypeBadge
      group={{ type: previousGroupType }}
      groupTypes={groupTypes}
    />
    <PersistentTypeExplanation />
  </Flex>
);

PersistedMoveGroupTypeTooltipBody.propTypes = {
  previousGroupType: PropTypes.string.isRequired,
  groupTypes: PropTypes.object.isRequired,
};

const PersistedMoveGroupTypeInfo = ({ group, testId }) => {
  const groupTypes = useGroupTypes();

  const { previousType, upstreamType } =
    group?.propertyOverrides?.groupType || {};
  const previousGroupType = upstreamType ?? previousType;

  if (!previousGroupType || group.type === previousGroupType) {
    return null;
  }

  const tooltip = (
    <PersistedMoveGroupTypeTooltipBody
      previousGroupType={previousGroupType}
      groupTypes={groupTypes}
    />
  );

  return (
    <FlexItem
      flex="0 0 auto"
      alignSelf="center"
      role="button"
      tabIndex={0}
      aria-label="Persisted move information"
    >
      <Popover
        direction="bottom"
        width="320px"
        text={tooltip}
        ariaLabel={ACCESSIBLE_LABEL}
      >
        <PersistedMoveIndicator
          testId={testId}
          icon={["far", "square-a-lock"]}
        />
      </Popover>
    </FlexItem>
  );
};

PersistedMoveGroupTypeInfo.propTypes = {
  group: PropTypes.object.isRequired,
  testId: PropTypes.string,
};

export default PersistedMoveGroupTypeInfo;
